import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, WhatsappLogo, Copy, Storefront } from "@phosphor-icons/react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function ResellerLanding() {
  const { slug } = useParams();
  const [reseller, setReseller] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({ company_name: "", owner_name: "", owner_email: "", owner_password: "", owner_cpf: "", owner_phone: "" });
  const [checkout, setCheckout] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/reseller/${slug}`);
        setReseller(data);
        if (data.plans?.length === 1) setSelectedPlan(data.plans[0]);
        // Persist reseller attribution across page navigations (anti-cheat)
        try { localStorage.setItem("zm_reseller_slug", slug); } catch (e) { console.warn("[reseller] could not persist slug:", e); }
      } catch (e) { console.warn("[reseller] landing not found:", e); setNotFound(true); }
    })();
  }, [slug]);

  useEffect(() => {
    if (!checkout?.purchase_id || checkout.status === "approved") return;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/public/signup/${checkout.purchase_id}`);
        setCheckout((prev) => ({ ...prev, ...data }));
        if (data.status === "approved") { toast.success("Pagamento aprovado! Sua conta foi criada."); clearInterval(t); }
      } catch (e) { console.debug("[reseller] checkout poll error (retry):", e?.message || e); }
    }, 5000);
    return () => clearInterval(t);
  }, [checkout?.purchase_id, checkout?.status]);

  const submit = async () => {
    if (!selectedPlan) return toast.error("Escolha um plano");
    if (!form.owner_email || !form.owner_password || !form.owner_name || !form.company_name || !form.owner_cpf) return toast.error("Preencha todos os campos obrigatórios");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/reseller/${slug}/signup`, {
        plan_id: selectedPlan.id,
        ...form,
        owner_cpf: form.owner_cpf.replace(/\D/g, ""),
      });
      setCheckout(data);
      toast.success("Pix gerado. Escaneie no seu banco.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao gerar Pix");
    } finally { setSubmitting(false); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(checkout.qr_code || "");
    setCopied(true); toast.success("Código Pix copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F5]">
        <div className="text-center">
          <Storefront size={64} className="mx-auto text-[#5C5C59] opacity-40" />
          <h1 className="text-3xl font-black mt-4">Página não encontrada</h1>
          <p className="text-[#5C5C59] mt-2">Confira o link enviado pelo seu revendedor.</p>
        </div>
      </div>
    );
  }
  if (!reseller) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7F7F5]"><span className="text-[#5C5C59]">Carregando…</span></div>;
  }
  const brand = reseller.brand_color;

  if (checkout && checkout.status === "approved") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F5] p-6">
        <div className="bg-white border-2 shadow-brutal p-10 max-w-lg text-center" style={{ borderColor: brand }}>
          <CheckCircle size={64} weight="fill" style={{ color: brand }} className="mx-auto" />
          <h1 className="text-3xl font-black mt-4">Conta criada com sucesso!</h1>
          <p className="text-[#5C5C59] mt-2">Faça login com o e-mail e senha que você cadastrou.</p>
          <p className="text-sm mt-4 p-3 bg-[#F7F7F5] border-l-[3px]" style={{ borderColor: brand }}>
            E-mail: <b>{form.owner_email}</b>
          </p>
          <Button className="mt-6 rounded-none w-full text-white" style={{ background: brand }} onClick={() => (window.location.href = "/login")}>
            Entrar no painel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F5]">
      <header className="p-8 border-b-2" style={{ borderColor: brand, background: "white" }}>
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          {reseller.logo_url ? (
            <img src={reseller.logo_url} alt={reseller.name} className="h-14 w-14 object-cover" />
          ) : (
            <div className="h-14 w-14 flex items-center justify-center" style={{ background: brand }}>
              <WhatsappLogo size={30} weight="fill" className="text-white" />
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ color: brand }}>Parceiro autorizado</p>
            <h2 className="text-2xl font-black">{reseller.name}</h2>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-8">
        <div className="mb-10">
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">{reseller.landing_headline}</h1>
          <p className="text-lg text-[#5C5C59] mt-3">{reseller.landing_subtitle}</p>
        </div>

        {!checkout && (
          <>
            {reseller.plans.length === 0 && (
              <div className="p-6 bg-[#FEF3C7] border-l-[3px] border-[#D97706]">
                <p className="font-semibold">Este revendedor ainda não configurou planos de venda.</p>
                <p className="text-sm text-[#5C5C59] mt-1">Peça para ele acessar o painel e cadastrar pelo menos um plano.</p>
              </div>
            )}

            {reseller.plans.length > 0 && (
              <>
                <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#5C5C59] mb-3">1. Escolha seu plano</p>
                <div className="grid md:grid-cols-3 gap-4 mb-10">
                  {reseller.plans.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPlan(p)}
                      data-testid={`plan-${p.id}`}
                      className={`text-left p-5 border-2 bg-white transition-all ${selectedPlan?.id === p.id ? "shadow-brutal" : "border-[#E5E5E0] hover:border-[#C7C7C0]"}`}
                      style={{ borderColor: selectedPlan?.id === p.id ? brand : undefined }}
                    >
                      <h3 className="text-xl font-black">{p.name}</h3>
                      <p className="text-3xl font-black tracking-tighter my-2" style={{ color: brand }}>R$ {p.price.toFixed(2)}<span className="text-sm text-[#5C5C59] font-normal">/mês</span></p>
                      <ul className="text-xs text-[#5C5C59] space-y-1 mt-3">
                        <li>• {p.max_attendants} atendentes</li>
                        <li>• {p.max_connections} conexões WhatsApp</li>
                        {(p.features || []).map((f, i) => <li key={i}>• {f}</li>)}
                      </ul>
                    </button>
                  ))}
                </div>

                {selectedPlan && (
                  <div className="bg-white border border-[#E5E5E0] shadow-brutal p-6">
                    <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#5C5C59] mb-4">2. Seus dados</p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs uppercase font-semibold tracking-wider">Nome da sua empresa</Label>
                        <Input data-testid="signup-company-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-none mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase font-semibold tracking-wider">Seu nome completo</Label>
                        <Input data-testid="signup-name-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="rounded-none mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase font-semibold tracking-wider">E-mail</Label>
                        <Input data-testid="signup-email-input" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="rounded-none mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase font-semibold tracking-wider">Senha (mín. 6 chars)</Label>
                        <Input data-testid="signup-password-input" type="password" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} className="rounded-none mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase font-semibold tracking-wider">CPF (para o Pix)</Label>
                        <Input data-testid="signup-cpf-input" value={form.owner_cpf} onChange={(e) => setForm({ ...form, owner_cpf: e.target.value })} className="rounded-none mt-1 font-mono" placeholder="000.000.000-00" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase font-semibold tracking-wider">WhatsApp (opcional)</Label>
                        <Input data-testid="signup-phone-input" value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} className="rounded-none mt-1 font-mono" placeholder="(11) 99999-9999" />
                      </div>
                    </div>
                    <Button onClick={submit} disabled={submitting} data-testid="signup-submit-btn" className="mt-5 rounded-none w-full text-white text-base py-6" style={{ background: brand }}>
                      {submitting ? "Gerando Pix…" : `Pagar R$ ${selectedPlan.price.toFixed(2)} via Pix e criar minha conta`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {checkout && checkout.status !== "approved" && (
          <div className="bg-white border-2 shadow-brutal p-6" style={{ borderColor: brand }} data-testid="signup-pix-result">
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#5C5C59]">3. Pague com Pix</p>
            <p className="text-2xl font-black mt-1">R$ {checkout.amount_brl.toFixed(2)} — {checkout.plan_name}</p>
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div>
                <p className="text-[10px] uppercase font-bold tracking-widest text-[#5C5C59]">Escaneie no seu banco</p>
                {checkout.qr_code_base64 && (
                  <img src={`data:image/png;base64,${checkout.qr_code_base64}`} alt="Pix QR" className="w-full max-w-[280px] border mt-2" />
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold tracking-widest text-[#5C5C59]">Ou copie o código</p>
                <div className="mt-2 p-3 bg-[#F7F7F5] border border-[#E5E5E0] font-mono text-[11px] break-all max-h-32 overflow-y-auto">
                  {checkout.qr_code}
                </div>
                <Button onClick={copyCode} className="rounded-none w-full mt-2 text-white" style={{ background: brand }} data-testid="signup-pix-copy">
                  <Copy size={14} weight="bold" className="mr-2" /> {copied ? "Copiado!" : "Copiar código"}
                </Button>
                <div className="mt-3 p-3 bg-[#FEF3C7] border-l-[3px] border-[#D97706] text-xs">
                  Após pagar, sua conta é criada automaticamente. Esta página atualiza a cada 5s.
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="text-center text-xs text-[#5C5C59] py-6">
        Powered by <b style={{ color: brand }}>{reseller.name}</b>
      </footer>
    </div>
  );
}
